import type { SectionAssignment } from '@breakoutlearning/firebase-repository/models/SectionAssignment'
import type { useTranslationTyped } from 'i18n/i18n'
import { DateTime } from 'luxon'

export function calculateDBDatesFromFormDates({
  dueDate,
  allowLateSubmissions,
  lateSubmissionCutoff,
}: {
  dueDate: DateTime | undefined
  allowLateSubmissions: boolean | undefined
  lateSubmissionCutoff: DateTime | undefined
}) {
  let expiresAt: DateTime | undefined
  if (allowLateSubmissions && lateSubmissionCutoff) {
    expiresAt = lateSubmissionCutoff
  } else {
    expiresAt = dueDate
  }

  let assignmentOverdueAt: DateTime | undefined
  if (allowLateSubmissions && dueDate) {
    assignmentOverdueAt = dueDate
  } else {
    assignmentOverdueAt = undefined
  }

  return {
    expiresAt,
    assignmentOverdueAt,
  }
}

export function checkScalarsAreEqual(
  a: SectionAssignment['data']['assignmentGradingScalars'] | null,
  b: SectionAssignment['data']['assignmentGradingScalars'] | null
) {
  // if scalars are both falsey or exactly equal
  if (a === b || (!a && !b)) return true

  // if one is falsey but not the other
  if (!a || !b) return false

  // compare individual fields
  return (
    a.attendanceScore === b.attendanceScore &&
    a.participationScore === b.participationScore &&
    a.quizScore === b.quizScore &&
    a.totalRubricScore === b.totalRubricScore
  )
}

export const defaultPeerReviewCriteria = (
  tt: ReturnType<typeof useTranslationTyped>['tt']
) => [
  {
    prQuestion:
      tt.instructor_assignment.configure_assignment_dialog.default_criteria.preparation_engagement.name(),
    prDescription:
      tt.instructor_assignment.configure_assignment_dialog.default_criteria.preparation_engagement.description(),
  },
  {
    prQuestion:
      tt.instructor_assignment.configure_assignment_dialog.default_criteria.contribution.name(),
    prDescription:
      tt.instructor_assignment.configure_assignment_dialog.default_criteria.contribution.description(),
  },
  {
    prQuestion:
      tt.instructor_assignment.configure_assignment_dialog.default_criteria.communication.name(),
    prDescription:
      tt.instructor_assignment.configure_assignment_dialog.default_criteria.communication.description(),
  },
  {
    prQuestion:
      tt.instructor_assignment.configure_assignment_dialog.default_criteria.integrity_honesty.name(),
    prDescription:
      tt.instructor_assignment.configure_assignment_dialog.default_criteria.integrity_honesty.description(),
  },
]

export function getDefaultLateSubmissionCutoff({
  dueDate,
  sectionEndDate,
}: {
  dueDate: DateTime | undefined
  sectionEndDate?: DateTime
}): DateTime | undefined {
  if (!dueDate) return undefined

  const oneWeekAfterDueDate = dueDate.plus({ weeks: 1 })

  // If sectionEndDate exists and one week after due date would exceed it, return undefined
  if (sectionEndDate && oneWeekAfterDueDate > sectionEndDate) {
    return undefined
  }

  return oneWeekAfterDueDate
}

/**
 * The window the due-date picker offers: at least 15 minutes after the start
 * and never in the past, capped at a year after the start. The cap is dropped
 * when it would fall before the floor (an assignment that started over a year
 * ago), which otherwise leaves nothing selectable.
 */
export function getDueDateBounds({
  assignedAt,
  now = DateTime.now(),
}: {
  assignedAt: DateTime | undefined
  now?: DateTime
}): { min: DateTime; max: DateTime | undefined } {
  const min = assignedAt
    ? DateTime.max(assignedAt.plus({ minutes: 15 }), now)
    : now
  const cap = assignedAt?.plus({ days: 365 })
  return { min, max: cap && cap > min ? cap : undefined }
}
